import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';

// Shared multi-select dropdown — the platform's answer to "a wall of chip
// buttons". A trigger button opens a portal popover with a checkbox list
// (searchable past a handful of options), matching the ColumnsMenu popover
// recipe exactly: hairline border, 2px radius, same shadow, close on
// click-away / Escape. Selection state stays OUTSIDE (a ReadonlySet) so a
// caller can route different option kinds to different stores (see the
// Mandate panel's sub-segment vs specialty split).

export interface MultiSelectOption {
  token: string;
  label: string;
  /** Section heading rendered above this option when it differs from the
   *  previous option's heading (grouped lists). */
  heading?: string;
  /** Indent one step — nested child options (e.g. fine specialties). */
  indent?: boolean;
}

const SEARCH_THRESHOLD = 8;

export function MultiSelect({
  label,
  options,
  selected,
  onToggle,
  onClear,
  disabled,
}: {
  /** Field label, shown on the trigger when nothing is selected. */
  label: string;
  options: ReadonlyArray<MultiSelectOption>;
  selected: ReadonlySet<string>;
  onToggle: (token: string) => void;
  onClear?: () => void;
  disabled?: boolean;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, q]);

  const summary = useMemo(() => {
    if (selected.size === 0) return null;
    const labels = options.filter((o) => selected.has(o.token)).map((o) => o.label);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.length} selected`;
  }, [options, selected]);

  const rect = open ? anchorRef.current?.getBoundingClientRect() : undefined;
  const searchable = options.length > SEARCH_THRESHOLD;

  let lastHeading: string | undefined;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          font: 'inherit',
          fontSize: 'var(--t-body)',
          padding: '3px var(--s-2)',
          border: '1px solid var(--hairline)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--surface)',
          color: summary ? 'var(--text-primary)' : 'var(--text-tertiary)',
          cursor: disabled ? 'default' : 'pointer',
          maxWidth: '100%',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary ?? label}
        </span>
        {selected.size > 0 && (
          <span
            style={{
              background: 'var(--accent)',
              color: 'var(--bg)',
              borderRadius: 8,
              padding: '0 6px',
              fontSize: 'var(--t-meta)',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {selected.size}
          </span>
        )}
        <ChevronDown size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={label}
            aria-multiselectable
            style={{
              position: 'fixed',
              top: Math.min(rect.bottom + 4, window.innerHeight - 340),
              left: Math.min(rect.left, Math.max(8, window.innerWidth - 288)),
              width: 280,
              maxHeight: 336,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--bg)',
              border: '1px solid var(--hairline)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
              zIndex: 'var(--z-toast)' as unknown as number,
              fontSize: 'var(--t-body)',
            }}
          >
            {searchable && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px var(--s-2)',
                  borderBottom: '1px solid var(--hairline)',
                }}
              >
                <Search size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                <input
                  autoFocus
                  value={q}
                  placeholder="Search…"
                  onChange={(e) => setQ(e.target.value)}
                  style={{
                    font: 'inherit',
                    fontSize: 'var(--t-body)',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    width: '100%',
                  }}
                />
              </div>
            )}
            <div style={{ overflowY: 'auto', padding: 'var(--s-1)', flex: 1, minHeight: 0 }}>
              {filtered.length === 0 && (
                <div
                  style={{
                    padding: '6px',
                    color: 'var(--text-tertiary)',
                    fontSize: 'var(--t-meta)',
                  }}
                >
                  No matches.
                </div>
              )}
              {filtered.map((o) => {
                const on = selected.has(o.token);
                const heading = o.heading !== lastHeading ? o.heading : undefined;
                lastHeading = o.heading;
                return (
                  <div key={o.token}>
                    {heading && (
                      <div
                        style={{
                          fontSize: 'var(--t-meta)',
                          color: 'var(--text-tertiary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          padding: '6px 6px 2px',
                        }}
                      >
                        {heading}
                      </div>
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => onToggle(o.token)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        textAlign: 'left',
                        font: 'inherit',
                        fontSize: 'var(--t-body)',
                        padding: '5px 6px',
                        paddingLeft: o.indent ? 22 : 6,
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        background: 'transparent',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                          borderRadius: 'var(--radius-sm)',
                          border: `1px solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
                          background: on ? 'var(--accent)' : 'var(--surface)',
                          color: 'var(--bg)',
                        }}
                      >
                        {on && <Check size={10} strokeWidth={3} />}
                      </span>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {o.label}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
            {onClear && selected.size > 0 && (
              <div
                style={{
                  borderTop: '1px solid var(--hairline)',
                  padding: 'var(--s-1)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  onClick={onClear}
                  style={{
                    font: 'inherit',
                    fontSize: 'var(--t-meta)',
                    color: 'var(--text-secondary)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px 6px',
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
